import _ from 'lodash';
import * as rxjs from 'rxjs';
import * as Elevated from '@pitaman71/omniglot-introspect';

import Dependencies from '../Dependencies';
import * as Mutations from '../Mutations';
import * as Stores from '../Stores';
import * as Streams from '../Streams';
import * as Objects from '../Objects';

const __moduleName__ = 'omniglot-live-data.Clients.RelationClient';

export interface State<BindingType extends Objects.BindingType> { 
    entries: Array<BindingType>, 
    status: Streams.Status
};

function dedup<BindingType extends Objects.BindingType>(name: string, symbols: (keyof BindingType)[], _entries: BindingType[], entries_: BindingType[]) {
    if(_entries === entries_) return _entries;
    const result: BindingType[] = [];
    const seen = new Set<string>();

    entries_.forEach(entry_ => {
        const keys = [... symbols.map(symbol => symbol.toString())];
        keys.sort();
        const missing = keys.reduce<string[]>((result, key) => (
            entry_[key] === undefined ? [ ...result, key ] : result
        ), []);
        if(missing.length > 0) {
            console.error(`RelationClient.dedup(${name}) rejects entry ${JSON.stringify(entry_)} which does not assign ${JSON.stringify(missing)}`)
        } else {
            const asString = keys.reduce((_asString, key) => (
                !entry_[key].objectId ? _asString : (_asString === "" ? "" : _asString+',')+key+"="+entry_[key].objectId
            ), "");
            if(!seen.has(asString)) {
                seen.add(asString);
                result.push(entry_)
            }
        }
    });
    return result;
}

export abstract class RelationClient<BindingType> {
    abstract assign(newValues: BindingType[]): this;
    abstract clear(): this;
    abstract insert(...newValues: BindingType[]): this;
    abstract remove(...newValues: BindingType[]): this;

    static debugMode = false;

    static fromActions<RelationBindingType extends Objects.BindingType>(
        name: string,
        stream: Streams.RelationStream<RelationBindingType>,
        on: {
            clear: () => void,
            insert: (entry_: RelationBindingType) => void,
            remove: (entry_: RelationBindingType) => void,
        }
    ) {
        return stream.watch(name).mutations({ 
            next: (mutation_) => {
                if(mutation_.clear) on.clear();
                else if(mutation_.assign) {
                    on.clear();
                    mutation_.assign.entries.forEach(entry_ => on.insert(entry_));
                } else if(mutation_.insert) {
                    mutation_.insert.entries.forEach(entry_ => on.insert(entry_));
                } else if(mutation_.remove) {
                    mutation_.remove.entries.forEach(entry_ => on.remove(entry_));
                }
            },
            error: () => {},
            complete: () => {}
        })
    }

    static fromDelta<BindingType extends Objects.BindingType>(
        name: string,
        setValues: (delta: (_entries: BindingType[]) => BindingType[]) => void,
        setStatus?: (delta: (_status: Streams.Status) => Streams.Status) => void
    ) {
        const __className__ = `Mutations.OfRelation.fromDelta`;
        return new class implements RelationClient<BindingType> {    
            subscriptions = new Streams.Subscriptions();
            entries: undefined|BindingType[];
            _watch(stream: Streams.RelationStream<BindingType>) {
                this.subscriptions.unsubscribe();
                if(RelationClient.debugMode)
                    console.log(`RelationClient(${name}) begins listening to ${stream.persistKey}`);
                this.subscriptions = stream.watch(name).mutations({
                    next: (mutation) => {
                        if(RelationClient.debugMode)
                            console.log(`RelationClient(${name}) receives ${mutation}`);
                        if(mutation.revert) {
                            setValues(_values => []);
                            this._watch(stream);
                        } else if(mutation.status) {
                            const status_ = mutation.status.status;
                            if(setStatus) setStatus(_status => status_);
                        } else {
                            mutation.dispatch(this); 
                        }
                    }, 
                    complete: () => {},
                    error: (error: any) => {
                        console.error(`${__className__} subscription to ${stream.persistKey} receives error ${error}`)
                    }
                });
                return this.subscriptions;
            }

            watchParent(stream: Streams.RelationStream<BindingType>): Streams.Subscriptions {
                return this._watch(stream);
            }
            assign(entries_: BindingType[]) {
                const updated = entries_.reduce<BindingType[]>((prev, entry_, index) => (
                    Objects.contains(prev, entry_) ? prev : [ ...prev, entry_]
                ), []);
                if(!_.isEqual(updated, this.entries || [])) {
                    this.entries = [ ...updated  ];
                    setValues(_entries => updated);
                }
                return this;
            }
            clear() {
                this.entries = undefined;
                setValues(old => []);
                return this;
            }
            insert(...entries_: BindingType[]) {
                const updated = entries_.reduce((prev, entry_, index) => (
                    Objects.contains(prev, entry_) ? prev : [ ...prev, entry_]
                ), this.entries || []);
                if(!_.isEqual(updated, this.entries || [])) {
                    this.entries = [ ...updated ];
                    setValues(_entries => updated);
                }
                return this;
            }

            remove(...entries_: BindingType[]) {
                const result = (this.entries || []).filter(_entry => !Objects.contains(entries_, _entry));
                this.entries = result;
                setValues(_entries => result);
                return this;
            }
        }();
    }
    static Eager<BindingType extends Objects.BindingType>(name_:string) {
        return new class _Variable {
            _name: string;
            _current: State<BindingType>;
            _downstream: rxjs.Subject<State<BindingType>>;
            _client?: RelationClient<BindingType>;

            constructor(name: string) {
                this._name = name;
                this._current = { entries: [], status: {} };
                this._downstream = Dependencies.makeSubject();
                this._client = undefined;
            }
            entries() { return [...this._current.entries]; }
            status() { return this._current.status; }
            authorize(status: boolean) {
                this._current.status.authorized = status;
                if(!status) this.set([]);
            }
            set(entries_: Array<BindingType>, status_?: Streams.Status) { 
                if(RelationClient.debugMode)
                    console.log(`Eager CHANGE ${this._name} ${this._current.entries} => ${entries_}`);
                this._current.entries = [...entries_];
                if(status_ !== undefined) this._current.status = status_;
                this._downstream.next({...this._current});
            }
            error(err: any) {
                this._downstream.error(err);
            }
            complete() {
                this._downstream.complete();
            }
            upstream(
                stream_: Streams.RelationStream<BindingType>
            ): _Variable {
                const client = RelationClient.fromDelta<BindingType>(this._name, delta => {
                    const entries_ = dedup(this._name, stream_.anchor.descriptor.getSymbols({ from: true, to: true }), this._current.entries, delta(this._current.entries))
                    this.set(entries_);
                });
                const subscription = client.watchParent(stream_);
                this._client = stream_.stateful();
                return this;
            }
            watch(observer: rxjs.Observer<State<BindingType>>) {
                return this._downstream.subscribe(observer);
            }
            change(): undefined|RelationClient<BindingType> {
                return this._client;
            }
        }(name_);
    }
}
